import {
  Command,
  CommandDialog,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDebounce } from 'react-use'
import { getSearchResults } from '../api/search'
import { PulseLoader } from 'react-spinners'
import CharacterClassIcon from './CharacterClassIcon'
import RealmSelect from './RealmSelect'
import { Link } from 'react-router-dom'
import { Button, buttonVariants } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import FactionIcon from './FactionIcon'
import { displayErrorToast } from '../helpers'
import useReferenceStore from '../state/useReferenceStore'
import { useContrastedBackground } from './Card'

const SearchOfficialApiButton = ({
  selectedRealm,
  searchingBlizzard,
  onSearch,
  className = '',
}: {
  selectedRealm?: wowaudit.Realm
  searchingBlizzard?: boolean
  onSearch: () => void
  className?: string
}) => {
  const { t } = useTranslation()

  return (
    <div className=''>
      <Tooltip delayDuration={150}>
        {selectedRealm ? (
          <Button
            variant='outline'
            size='sm'
            className={className}
            loading={searchingBlizzard}
            disabled={searchingBlizzard === false}
            spinner='blizzard'
            onClick={onSearch}
          >
            {searchingBlizzard === false
              ? t(`header.official-api-searched`)
              : t(`header.search-official-api`)}
          </Button>
        ) : (
          <TooltipTrigger
            className={`${buttonVariants({
              variant: 'outline',
              size: 'sm',
            })} ${className} cursor-not-allowed opacity-50`}
          >
            {t(`header.search-official-api`)}
          </TooltipTrigger>
        )}

        <TooltipContent>
          <p>{t('header.choose-realm-first')}</p>
        </TooltipContent>
      </Tooltip>
    </div>
  )
}

interface Props<T> {
  placeholder: string
  type: 'inline' | 'dialog'
  setOpen?: (value: boolean) => void
  searchType?: 'character' | 'guild' | 'community'
  actions?: (result: T) => React.ReactNode
  listClassName?: string
  itemClassName?: string
}

type SearchResult =
  | wowaudit.CharacterReference
  | wowaudit.GuildReference
  | wowaudit.CommunityReference

const SearchBar = <T extends SearchResult>({
  placeholder,
  type,
  setOpen,
  searchType,
  actions,
  listClassName,
  itemClassName,
}: Props<T>) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [initiallyLoaded, setInitiallyLoaded] = useState(false)
  const [searchingBlizzard, setSearchingBlizzard] = useState<boolean>()
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [selectedRealm, setSelectedRealm] = useState<wowaudit.Realm>()
  const [results, setResults] = useState<T[]>([])
  const { storeReferences } = useReferenceStore()
  const background = useContrastedBackground()

  const CommandComponent = type === 'inline' ? Command : CommandDialog
  const commandProps =
    type === 'inline'
      ? {}
      : {
          open: true,
          onOpenChange: setOpen,
          maxWidth: '40rem',
          centered: false,
        }

  useDebounce(
    () => {
      if (searchTerm.length > 1) {
        fetchResults('internal')
      } else {
        setLoading(false)
      }
    },
    200,
    [searchTerm, selectedRealm],
  )

  const handleInputChange = (term: string) => {
    if (term.length < 2) {
      setResults([])
      setInitiallyLoaded(false)
    } else {
      setLoading(true)
    }
  }

  // TODO: can we use react-query in a nice way for this (more) complex use case?
  const fetchResults = (type: 'internal' | 'external') => {
    getSearchResults<T>(searchTerm, type, selectedRealm, searchType)
      .then((data) => {
        setResults(data)
        storeReferences(data)

        type === 'internal' ? setLoading(false) : setSearchingBlizzard(false)
        setInitiallyLoaded(true)
      })
      .catch((query) => {
        displayErrorToast(query, t)
        type === 'internal' ? setLoading(false) : setSearchingBlizzard(false)
      })
      .then(() => {
        if (type === 'internal') setSearchingBlizzard(undefined)
      })
  }

  return (
    <CommandComponent
      {...commandProps}
      shouldFilter={false}
      filter={() => 1}
      className='bg-transparent'
    >
      <div
        className={`flex items-center justify-between gap-0.5 ${
          type === 'dialog' ? 'flex-row-reverse' : background
        } border-b-2`}
      >
        <div
          className={`relative grow ${type === 'dialog' ? 'border-l-2' : ''}`}
        >
          <CommandInput
            onValueChange={(value) => {
              setSearchTerm(value)
              handleInputChange(value)
            }}
            value={searchTerm}
            placeholder={placeholder}
            className='h-[2.35rem] pr-8'
          />
          <div
            className={`absolute ${
              type === 'inline' ? 'right-[0.8rem]' : 'right-[2.4rem]'
            } ${type === 'inline' ? 'top-2' : 'top-[0.8rem]'} opacity-30`}
          >
            <PulseLoader
              loading={loading}
              speedMultiplier={0.65}
              size={10}
              color='var(--color-primary)'
            />
          </div>
        </div>

        <RealmSelect
          realm={selectedRealm}
          setRealm={(realm) => {
            setSelectedRealm(realm)
            handleInputChange(searchTerm)
          }}
          inModal={type === 'dialog'}
          isClearable={true}
          className={`border-0 ${
            type === 'dialog' ? 'ml-1' : 'rounded-l-none border-l-2'
          }`}
        />
      </div>

      <CommandList
        className={`${
          type === 'inline'
            ? 'h-[calc(var(--cmdk-list-height)+min(1rem,var(--cmdk-list-height)))]'
            : ''
        } ${listClassName ?? ''}`}
      >
        {searchTerm.length > 1 && initiallyLoaded && !results.length && (
          <div>
            <div className='py-6 text-center text-sm flex flex-col items-center gap-4'>
              <span>
                {t(
                  searchingBlizzard === false
                    ? 'header.no-search-results-blizzard'
                    : 'header.no-search-results',
                )}
              </span>

              <SearchOfficialApiButton
                selectedRealm={selectedRealm}
                searchingBlizzard={searchingBlizzard}
                onSearch={() => {
                  setSearchingBlizzard(true)
                  fetchResults('external')
                }}
                className={`inline ${
                  searchingBlizzard === false ? 'invisible' : ''
                }`}
              />
            </div>
          </div>
        )}

        {results.map((result, i) => {
          // Do we want to pass this data from the backend explicitly instead?
          const resultType =
            'characterClass' in result
              ? 'Character'
              : 'faction' in result
              ? 'Guild'
              : 'Community'

          const linkComponent = (
            children: React.ReactNode,
            target?: string,
          ) => (
            <Link
              className='w-full'
              to={result.defaultPath}
              onClick={() =>
                type === 'dialog' && setOpen ? setOpen(false) : null
              }
              target={target}
            >
              {children}
            </Link>
          )

          const resultContent = (
            <div className='flex w-full px-2 py-0.5 items-center justify-between gap-4 flex-wrap'>
              <div className='flex items-center gap-1 grow flex-wrap'>
                {'characterClass' in result ? (
                  <CharacterClassIcon
                    characterClass={result.characterClass}
                    className='mr-2'
                  />
                ) : (
                  <FactionIcon
                    faction={
                      'faction' in result
                        ? result.faction ?? undefined
                        : undefined
                    }
                    className='mr-2'
                  />
                )}

                <span className='grow flex items-center gap-2'>
                  {linkComponent(result.name, '_blank')}
                </span>
                {'realm' in result ? (
                  <span className='whitespace-nowrap'>
                    {result.realm.name} ({result.realm.region})
                  </span>
                ) : null}
              </div>

              <span className='text-muted-foreground'>
                {actions ? actions(result) : resultType}
              </span>
            </div>
          )

          return (
            <CommandItem
              key={i}
              value={result.name}
              className={`${itemClassName ?? ''} ${
                actions
                  ? 'hover:bg-transparent hover:text-unset cursor-default pointer-events-auto'
                  : ''
              }`}
            >
              {actions ? (
                <div className='w-full'>{resultContent}</div>
              ) : (
                linkComponent(resultContent)
              )}
            </CommandItem>
          )
        })}

        {searchTerm.length > 1 &&
          initiallyLoaded &&
          results.length > 0 &&
          (type === 'inline' || selectedRealm) && (
            <div className='p-2'>
              <SearchOfficialApiButton
                selectedRealm={selectedRealm}
                searchingBlizzard={searchingBlizzard}
                onSearch={() => {
                  setSearchingBlizzard(true)
                  fetchResults('external')
                }}
              />
            </div>
          )}
      </CommandList>
    </CommandComponent>
  )
}

export default SearchBar
